import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import PropTypes from 'prop-types';

import { bringBackNonRoutableRequestToPom } from 'store/thunks/workQueueThunks';

import { useNonRoutableResolution } from '../hooks/useNonRoutableResolution';
import { nonRoutableRetryIsSafe } from '../utils/nonRoutableRequestErrors';
import NonRoutableRequestIdentity from './NonRoutableRequestIdentity';
import NonRoutableResolutionOutcome from './NonRoutableResolutionOutcome';

// The row leaves the queue on success, because Bring Back drives it to RESOLVED and RESOLVED is
// not a filterable status on this read at all. Say so, or a row vanishing from a list of stuck
// prescriptions reads as something having gone wrong.
//
// THE REASON MATTERS MORE THAN THE CONCLUSION. It is NOT "the queue serves PENDING |
// RESOLUTION_IN_PROGRESS only" — that describes the DEFAULT view, and CANCELLED is filterable
// past it, which is exactly why the Cancel dialog can no longer make this promise. If RESOLVED
// were ever made filterable too, this sentence stops being true and the copy must change with
// it.
const successMessage = () =>
  'Released to POM. An order has been created and the request has left this queue.';

/**
 * BRING BACK TO POM (POM-6223).
 *
 * THIS CREATES AN ORDER THAT WILL BE DISPENSED, AND THERE IS NO UNDO. The parked request is
 * released into the normal POM flow by re-posting its stored message verbatim; routing is NOT
 * re-run, so whatever made it unroutable is not re-checked.
 *
 * The confirm button is destructive-coloured and the consequence is stated before it, not after.
 */
const BringBackNonRoutableRequestDialog = ({ open, onClose, item }) => {
  const { submit, submitting, outcome, dismiss } = useNonRoutableResolution({
    item,
    action: bringBackNonRoutableRequestToPom,
    successMessage,
    onClose,
    // Resets the outcome each time the dialog is opened. The row menu keeps both dialogs mounted,
    // so without this a previous attempt's alert would still be on screen with no confirm button.
    open,
  });

  // Every dismissal goes through `dismiss`, which refreshes the queue on the way out — the
  // refetch is deliberately NOT done when the mutation returns, because this dialog lives inside
  // the row it is resolving and the refresh would unmount it mid-message.
  const handleClose = (_, reason) => {
    if (reason === 'backdropClick' || submitting) return;
    dismiss();
  };

  // Once an attempt has come back, the confirm button is removed unless the backend explicitly
  // said the row was rolled back to Pending. Three of the codes it can return are raised AFTER
  // the prescription has already been released; leaving a clickable button under any of them is
  // how the same prescription gets dispensed twice.
  const canSubmit = !outcome || nonRoutableRetryIsSafe(outcome.described);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      aria-labelledby='BringBackNonRoutableRequestDialog-title'
    >
      <DialogTitle id='BringBackNonRoutableRequestDialog-title'>
        Bring this request back to POM?
      </DialogTitle>
      <DialogContent>
        <NonRoutableRequestIdentity item={item} />

        <Alert severity='warning'>
          <AlertTitle>This creates an order that will be dispensed</AlertTitle>
          The request is released into the normal POM flow exactly as it arrived. Routing is
          <strong> not </strong>
          re-run, so whatever stopped it being routed is not re-checked. This cannot be undone.
        </Alert>

        {outcome && <NonRoutableResolutionOutcome outcome={outcome} />}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={dismiss} disabled={submitting}>
          {outcome && !canSubmit ? 'Close' : 'Cancel'}
        </Button>
        {canSubmit && (
          <LoadingButton
            variant='contained'
            color='error'
            loading={submitting}
            onClick={submit}
            id={`BringBackNonRoutableRequestDialog-${item.nonRoutableRequestId}-confirm`}
          >
            Bring Back to POM
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

BringBackNonRoutableRequestDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.shape({
    nonRoutableRequestId: PropTypes.string.isRequired,
    referenceId: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
};

export default BringBackNonRoutableRequestDialog;
